import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Save, Search } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ImageUploader from "@/components/admin/ImageUploader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = {
    title: "",
    slug: "",
    summary: "",
    description: "",
    cover_image: "",
    gallery: [],
    demo_url: "",
    repo_url: "",
    category: "Web App",
    tech_stack: [],
    featured: false,
    status: "Completed",
    year: new Date().getFullYear(),
    client: "",
    role: "",
    order: 0,
};

export default function AdminProjects() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const res = await api.get("/projects");
        setItems(res.data);
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this project?")) return;
        try {
            await api.delete(`/admin/projects/${id}`);
            toast.success("Project deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const filtered = items.filter((p) => p.title.toLowerCase().includes(query.toLowerCase()) || p.category.toLowerCase().includes(query.toLowerCase()));

    return (
        <div data-testid="admin-projects-page">
            <Header
                title="Projects"
                subtitle="Kelola portfolio projects yang ditampilkan di halaman utama."
                action={
                    <button
                        onClick={() => setEditing({ ...empty })}
                        data-testid="project-new"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover"
                    >
                        <Plus size={16} /> New project
                    </button>
                }
            />

            <div className="mb-5 relative max-w-sm">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search projects…"
                    className="w-full bg-ink-800/60 border border-white/5 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder:text-ink-300 focus:outline-none focus:border-brand/50"
                />
            </div>

            {loading ? (
                <div className="mono-label">Loading…</div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-white/5">
                    <table className="w-full text-sm">
                        <thead className="bg-ink-800/60 text-ink-200">
                            <tr>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Project</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Category</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Status</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Year</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Featured</th>
                                <th className="text-right p-4 font-mono text-xs uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {filtered.map((p) => (
                                <tr key={p.id} className="hover:bg-white/5">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            {p.cover_image && (
                                                <img src={p.cover_image} alt="" className="w-12 h-12 rounded object-cover border border-white/5" />
                                            )}
                                            <div className="min-w-0">
                                                <div className="text-white font-medium truncate max-w-xs">{p.title}</div>
                                                <div className="text-xs text-ink-300 font-mono truncate">{p.slug}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 text-ink-100">{p.category}</td>
                                    <td className="p-4">
                                        <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${
                                            p.status === "Active" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" :
                                            p.status === "Maintenance" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" :
                                            p.status === "Archived" ? "bg-zinc-500/15 border-zinc-500/40 text-zinc-300" :
                                            "bg-white/5 border-white/15 text-ink-100"
                                        }`}>{p.status || "Completed"}</span>
                                    </td>
                                    <td className="p-4 text-ink-100 font-mono">{p.year}</td>
                                    <td className="p-4">
                                        {p.featured ? (
                                            <span className="font-mono text-xs px-2 py-0.5 rounded bg-brand/20 text-brand">Featured</span>
                                        ) : (
                                            <span className="text-ink-300 font-mono text-xs">—</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-1">
                                            <button
                                                onClick={() => setEditing({ ...p })}
                                                data-testid={`project-edit-${p.slug}`}
                                                className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                onClick={() => remove(p.id)}
                                                data-testid={`project-delete-${p.slug}`}
                                                className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="p-12 text-center text-ink-300">No projects found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <ProjectEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function ProjectEditor({ initial, onClose, onSaved }) {
    const [data, setData] = useState({ ...initial });
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const update = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };
    const updateList = (k) => (e) => {
        const v = e.target.value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        setData((s) => ({ ...s, [k]: v }));
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/admin/projects/${initial.id}`, data);
            } else {
                await api.post("/admin/projects", data);
            }
            toast.success(isEdit ? "Project updated" : "Project created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="project-editor">
            <div className="w-full max-w-3xl bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit project" : "New project"}</div>
                        <h2 className="font-display text-xl text-white truncate max-w-md">{data.title || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="project-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover disabled:opacity-60">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    <Card title="Basics">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Title"><input className="ainp" value={data.title} onChange={update("title")} /></Field>
                            <Field label="Slug (optional)"><input className="ainp" value={data.slug} onChange={update("slug")} placeholder="auto from title" /></Field>
                        </div>
                        <Field label="Summary"><input className="ainp" value={data.summary} onChange={update("summary")} /></Field>
                        <Field label="Full description"><textarea rows={6} className="ainp" value={data.description} onChange={update("description")} /></Field>
                    </Card>

                    <Card title="Media">
                        <Field label="Cover image URL"><ImageUploader value={data.cover_image} onChange={(v) => setData(s => ({...s, cover_image: v}))} placeholder="URL atau upload gambar..." /></Field>
                        <Field label="Gallery URLs (comma-separated)">
                            <textarea rows={3} className="ainp" value={(data.gallery || []).join(", ")} onChange={updateList("gallery")} placeholder="https://… , https://…" />
                        </Field>
                    </Card>

                    <Card title="Meta">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Category"><input className="ainp" value={data.category} onChange={update("category")} /></Field>
                            <Field label="Status">
                                <select className="ainp" value={data.status || "Completed"} onChange={update("status")} data-testid="project-status">
                                    <option value="Active">Active (ongoing)</option>
                                    <option value="Maintenance">Maintenance</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Archived">Archived</option>
                                </select>
                            </Field>
                            <Field label="Year"><input type="number" className="ainp" value={data.year} onChange={update("year")} /></Field>
                            <Field label="Client"><input className="ainp" value={data.client} onChange={update("client")} /></Field>
                            <Field label="Role"><input className="ainp" value={data.role} onChange={update("role")} /></Field>
                            <Field label="Demo URL"><input className="ainp" value={data.demo_url} onChange={update("demo_url")} /></Field>
                            <Field label="Repo URL"><input className="ainp" value={data.repo_url} onChange={update("repo_url")} /></Field>
                            <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={update("order")} /></Field>
                            <Field label="Tech stack (comma-separated)"><input className="ainp" value={(data.tech_stack || []).join(", ")} onChange={updateList("tech_stack")} /></Field>
                        </div>
                        <label className="flex items-center gap-3 pt-2 cursor-pointer">
                            <input type="checkbox" checked={data.featured} onChange={update("featured")} className="w-4 h-4 accent-brand" />
                            <span className="text-sm text-ink-100">Featured project</span>
                        </label>
                    </Card>
                </div>
            </div>
        </div>
    );
}
